import { AlertTriangle, ShieldAlert, Shield, ShieldCheck, Info } from "lucide-react";
import type { RiskLevel } from "@/lib/api";

interface RiskDistribution {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

interface RiskOverviewCardProps {
  riskLevel: RiskLevel;
  assessment: string;
  findingCount: number;
  distribution?: RiskDistribution;
  className?: string;
}

const RISK_CONFIG: Record<RiskLevel, {
  label: string;
  icon: typeof AlertTriangle;
  color: string;
  barColor: string;
}> = {
  critical: { label: "Critical Risk", icon: ShieldAlert, color: "text-red-400", barColor: "bg-red-500" },
  high: { label: "High Risk", icon: AlertTriangle, color: "text-orange-400", barColor: "bg-orange-500" },
  medium: { label: "Medium Risk", icon: Shield, color: "text-amber-400", barColor: "bg-amber-500" },
  low: { label: "Low Risk", icon: ShieldCheck, color: "text-cyan-400", barColor: "bg-cyan-500" },
  info: { label: "Excellent", icon: Info, color: "text-emerald-400", barColor: "bg-emerald-500" },
};

const SEVERITY_BARS: { key: keyof RiskDistribution; color: string; label: string }[] = [
  { key: "critical", color: "bg-red-500", label: "Critical" },
  { key: "high", color: "bg-orange-500", label: "High" },
  { key: "medium", color: "bg-amber-500", label: "Medium" },
  { key: "low", color: "bg-cyan-500", label: "Low" },
  { key: "info", color: "bg-emerald-500", label: "Info" },
];

export function RiskOverviewCard({
  riskLevel,
  assessment,
  findingCount,
  distribution,
  className = "",
}: RiskOverviewCardProps) {
  const config = RISK_CONFIG[riskLevel];
  const Icon = config.icon;
  const total = distribution ? Object.values(distribution).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className={`rounded-xl border border-border bg-card/50 p-5 ${className}`}>
      {/* Compact horizontal layout */}
      <div className="flex items-center gap-4 mb-4">
        <div className={`p-2.5 rounded-lg bg-muted/50 border border-border`}>
          <Icon className={`h-6 w-6 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className={`text-xl font-bold ${config.color}`}>{config.label}</h2>
          <p className="text-sm text-muted-foreground">{assessment}</p>
        </div>
        <div className="text-right shrink-0">
          <span className="text-2xl font-bold font-mono">{findingCount}</span>
          <p className="text-xs text-muted-foreground">{findingCount === 1 ? "finding" : "findings"}</p>
        </div>
      </div>

      {/* Distribution bar */}
      {distribution && total > 0 && (
        <div>
          <div className="flex h-2 rounded-full overflow-hidden bg-muted/50 mb-2">
            {SEVERITY_BARS.map(({ key, color }) => {
              const count = distribution[key];
              if (count === 0) return null;
              return (
                <div
                  key={key}
                  className={`${color} transition-all`}
                  style={{ width: `${(count / total) * 100}%` }}
                />
              );
            })}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {SEVERITY_BARS.map(({ key, color, label }) => {
              const count = distribution[key];
              if (count === 0) return null;
              return (
                <div key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={`w-2 h-2 rounded-full ${color}`} />
                  <span>{count} {label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default RiskOverviewCard;
